import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import TwoFactorChallenge from "./TwoFactorChallenge";

/**
 * Permanently deletes a manager: the manager's own email typed out, then a
 * fresh single-use 2FA proof, both enforced by DELETE /api/users/:id. Shared by the owner's Users page and the
 * superadmin's user list.
 */
const DeleteManagerModal: React.FC<{
  user: { id: number; name?: string | null; email: string };
  onDeleted: (id: number) => void;
  onClose: () => void;
  /** Superadmin only: the locations this manager still works at. Removing an
   *  active manager is normally the owner's call, so the superadmin is told. */
  stillAt?: string[];
}> = ({ user, onDeleted, onClose, stillAt }) => {
  const { t } = useTranslation();
  const [confirmInput, setConfirmInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<("totp" | "email" | "backup")[] | null>(null);

  const submit = async (twoFactorToken?: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch(`/api/users/${user.id}`, {
        method: "DELETE",
        body: JSON.stringify({ confirm: confirmInput, twoFactorToken }),
      });
      const data = await res?.json().catch(() => ({}));
      if (res?.status === 403 && data?.error === "2FA_REQUIRED") {
        setChallenge(data.methods || ["email"]);
      } else if (res?.ok) {
        onDeleted(user.id);
        onClose();
        return;
      } else {
        setError(data?.error || t("users.deleteFailed"));
      }
    } catch {
      setError(t("users.deleteFailed"));
    }
    setLoading(false);
  };

  if (challenge) {
    return (
      <TwoFactorChallenge
        methods={challenge}
        onCancel={() => { setChallenge(null); setLoading(false); }}
        onVerified={(token) => { setChallenge(null); submit(token); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm mx-auto space-y-3">
        <h2 className="text-xl font-bold text-red-700">{t("users.deleteUserTitle")}</h2>
        <p className="text-sm text-gray-700">{t("users.deleteUserWarning", { name: user.name || user.email, email: user.email })}</p>
        {stillAt && stillAt.length > 0 && (
          <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">
            {t("users.deleteStillAtLocation", { locations: stillAt.join(", ") })}
          </p>
        )}
        <label className="block text-sm text-gray-700">
          {t("users.deleteUserTypeToConfirm")} <b>{user.email}</b>
        </label>
        <input
          type="text"
          autoComplete="off"
          className="w-full border rounded px-3 py-2"
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value)}
          disabled={loading}
        />
        {error && <div className="text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-2">
          <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onClick={onClose} disabled={loading}>
            {t("common.cancel")}
          </button>
          <button
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            onClick={() => submit()}
            disabled={loading || confirmInput.trim().toLowerCase() !== user.email.toLowerCase()}
          >
            {loading ? "..." : t("users.delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteManagerModal;
